import type { QuotePassenger, QuoteRecord } from '../types';
import { approximateDateOfBirthFromAge, normalizeDateOfBirth } from './passengerBirthDate';
import { ensureEticketNumber, sanitizeEticketNumber } from './skyluxReceiptIds';

function normalizeAge(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = Math.round(value);
    return n >= 1 && n <= 120 ? n : undefined;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Math.round(Number(value.trim()));
    return Number.isFinite(n) && n >= 1 && n <= 120 ? n : undefined;
  }
  return undefined;
}

/** Names and ages on the booking (no per-passenger email). */
export function normalizePassengers(quote: Pick<QuoteRecord, 'passengers' | 'clientSnapshot'>): QuotePassenger[] {
  const fromList = (quote.passengers ?? [])
    .map((p) => {
      const name = (p.name ?? '').trim();
      const age = normalizeAge(p.age);
      const dateOfBirth = normalizeDateOfBirth(p.dateOfBirth);
      const eTicketNumber = p.eTicketNumber?.trim();
      return name
        ? {
            name,
            ...(age != null ? { age } : {}),
            ...(dateOfBirth ? { dateOfBirth } : {}),
            ...(eTicketNumber ? { eTicketNumber } : {}),
          }
        : null;
    })
    .filter((p): p is QuotePassenger => p != null);
  if (fromList.length > 0) return fromList;
  const legacy = quote.clientSnapshot?.name?.trim();
  if (legacy) return [{ name: legacy }];
  return [];
}

export function passengersLabel(quote: Pick<QuoteRecord, 'passengers' | 'clientSnapshot'>, separator = ' · '): string {
  const list = normalizePassengers(quote);
  return list
    .map((p) => {
      const base = p.name.toUpperCase();
      const dob = p.dateOfBirth ? `, DOB ${p.dateOfBirth}` : '';
      return p.age != null ? `${base} (${p.age}${dob})` : base;
    })
    .join(separator);
}

export function resizePassengers(prev: QuotePassenger[], count: number, seedName = ''): QuotePassenger[] {
  const n = Math.max(1, Math.min(9, count));
  const next = prev.slice(0, n).map((p) => ({
    name: p.name ?? '',
    age: p.age,
    dateOfBirth: p.dateOfBirth,
  }));
  while (next.length < n) {
    next.push({ name: '', age: undefined, dateOfBirth: undefined });
  }
  if (seedName.trim() && !next[0]!.name.trim()) {
    next[0] = { name: seedName.trim(), age: next[0]!.age, dateOfBirth: next[0]!.dateOfBirth };
  }
  return next;
}

/** @deprecated Use resizePassengers */
export const resizePassengerNames = resizePassengers;

export function validatePassengers(passengers: QuotePassenger[]): string | null {
  if (passengers.length === 0) return 'Add at least one passenger.';
  for (let i = 0; i < passengers.length; i += 1) {
    const row = passengers[i]!;
    if (!row.name.trim()) {
      return `Enter the name for passenger ${i + 1} (as on passport).`;
    }
    const age = normalizeAge(row.age);
    if (age == null) {
      return `Enter the age for passenger ${i + 1}.`;
    }
  }
  return null;
}

/** @deprecated Use validatePassengers */
export const validatePassengerNames = validatePassengers;

/** One display row per traveler for e-ticket / quote emails. */
export function passengerTicketRows(
  quote: Pick<QuoteRecord, 'passengers' | 'clientSnapshot' | 'eTicketNumber'>,
): { name: string; eTicketNumber: string }[] {
  const travelers = normalizePassengers(quote);
  const fallback = ensureEticketNumber(quote.eTicketNumber);
  return travelers.map((p) => ({
    name: p.name.trim().toUpperCase(),
    eTicketNumber: sanitizeEticketNumber(p.eTicketNumber) ?? fallback,
  }));
}

export function passengersForQuoteRecord(
  passengers: QuotePassenger[],
  contact: { email: string; phone?: string },
  options?: { referenceTravelDate?: string },
): { passengers: QuotePassenger[]; clientSnapshot: QuoteRecord['clientSnapshot'] } {
  const refDate = options?.referenceTravelDate;
  const normalized = passengers.map((p) => {
    const age = normalizeAge(p.age)!;
    const dateOfBirth =
      normalizeDateOfBirth(p.dateOfBirth) ?? approximateDateOfBirthFromAge(age, refDate);
    return {
      name: p.name.trim().toUpperCase(),
      age,
      dateOfBirth,
    };
  });
  const primary = normalized[0]!.name;
  return {
    passengers: normalized,
    clientSnapshot: {
      name: primary,
      email: contact.email.trim(),
      phone: contact.phone?.trim() || undefined,
    },
  };
}
